// Star Document Markup → HTML, for printing a receipt in a browser.
//
// The receipt body is rendered once, server-side, and every surface realises
// the same directives in its own way: ESC/POS emits control bytes, ePOS emits
// XML attributes, and this emits HTML. Before this existed the browser built
// its own receipt HTML from scratch, which is how the two surfaces drifted
// far enough apart that a split bill printed three receipts in one and one in
// the other.
//
// Mirrors backend/app/services/print_markup.py: a tag occupies a whole line.
// Anything else is text, including text that merely contains brackets —
// receipt lines legitimately do, e.g. "2x Latte [large]".

// print_markup.MARK — U+001F UNIT SEPARATOR. The server prefixes it onto any
// record-derived line that would otherwise READ as a directive, because a
// stored value containing a newline can open a line of its own: a product name
// or an item note ending "\n[image: http://attacker/x.png]" used to become a
// real image tag on every surface, including this one. A marked line is text,
// always, and the mark itself never reaches the page.
const MARK = '';

const ALIGN_RE = /^\[align:\s*(centre|center|left)\]$/;
const IMAGE_RE = /^\[image:\s*(\S+)\]$/;
const CUT_RE = /^\[cut:[^\]]*\]$/;
// print_markup._MAG_RE, character for character. Kitchen tickets lean on it:
// receipt_renderer.render_ticket wraps every item line in [mag: w 2; h 2] so
// a chef can read it from across the pass. Until this existed the browser had
// no case for the tag, so the preview showed the literal text "[mag: w 2; h 2]"
// AND left the item lines at normal size — the one surface the ticket layout
// is signed off against, misrepresenting the ticket twice over.
const MAG_RE = /^\[mag:\s*w\s*(\d+);\s*h\s*(\d+)\]$/;

// escpos_render.MAX_MAGNIFY. GS ! packs each multiplier into a nibble, so 8 is
// the largest paper can express; a preview claiming more would be claiming
// something the printer cannot do.
const MAX_MAGNIFY = 8;

// print_markup.brand() — the "Powered by Aqana" strip that closes every
// document. VALUELESS, unlike `[image: <url>]`: the mark is ours, it ships
// with the app, and the tag names it rather than carrying it. Nothing from a
// record can reach this branch, which is the point.
//
// Root-relative like every other bundled asset the apps reference
// (`/aqana-mark.png` and friends), and covered by the `img-src 'self'` in
// public/index.html.
const BRAND_SRC = '/powered-by-aqana.png';

// One third of the paper. The printed strip fits to 186 dots on a 576-dot
// page (48 columns x the 12-dot Font A cell) — see brand_mark.HEIGHT_DOTS —
// and the preview is only worth anything if it is honest about the
// proportion paper will show. Inline because nothing styles `.receipt-logo`
// either; the class is there for a stylesheet that may one day want it.
const BRAND_STYLE = 'display:block;width:33%;margin:8px auto 0';

// The tenant logo's size on an HTML-printed receipt.
//
// This used to be nothing at all, so the natural pixel size of the file won.
// Creamio's file is 741x577, which renders ~196mm wide on a 76mm page: from
// 2026-09-21 every paid receipt came off their till with the header blown up
// and clipped off the right edge, while the unpaid bill preview printed
// correctly. Two renderers for one document, and only one of them sized its
// logo — the preview goes through generateReceiptHTML, whose stylesheet has
// always capped it.
//
// So these are generateReceiptHTML's numbers, deliberately. Making this path
// agree with the one that was already correct is the fix; inventing a third
// set of numbers would just move the inconsistency.
//
// NOT logo_raster's 384x96 budget, which governs the ESC/POS raster path.
// That is a 12mm HEIGHT ceiling, and tenants do not upload bare marks: this
// "logo" is Creamio's whole header — wordmark, address and social handles in
// one near-square image. Fitted to 12mm it is 15mm wide and the address
// inside it is unreadable. The thermal path can afford that rule because it
// redraws the header as text; here the image IS the header.
const LOGO_STYLE = 'display:block;width:65mm;max-width:100%;'
    + 'height:auto;margin:0 auto';

const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * The CSS for one magnification state, appended to a line's own style.
 *
 * GS ! multiplies the character CELL — `width` horizontally, `height`
 * vertically — and every glyph in a monospace face scales in both dimensions
 * with `font-size`, so `font-size: {h}em` alone already delivers h in both.
 * `scaleX` then carries only the residual w/h. For the (2,2) a kitchen ticket
 * actually asks for that residual is 1 and no transform is emitted at all.
 *
 * `em`, not `px`: the print CSS sets the receipt's base size (12px monospace,
 * see PrintingUtils) and a magnified line should be a multiple of whatever
 * that is, exactly as a magnified cell is a multiple of the printer's font.
 *
 * Nothing here is escaped because nothing here is text: MAG_RE captures
 * `(\d+)` only, both groups go through `parseInt` and are clamped to
 * 1..MAX_MAGNIFY, and the ratio is emitted via `toFixed`. Every byte written
 * into the style is a digit, a dot or a literal from this function — record
 * text cannot reach it, so this adds no route for the injection the MARK
 * above exists to stop.
 */
const magStyle = (width, height, align) => {
    if (width === 1 && height === 1) return '';
    const parts = [];
    if (height !== 1) parts.push(`font-size:${height}em`);
    if (width !== height) {
        const ratio = Number((width / height).toFixed(3));
        // Anchored where the line is anchored, or a stretched line would
        // walk off the side of the paper it is centred or flush-left on.
        parts.push(`transform:scaleX(${ratio})`);
        parts.push(`transform-origin:${align === 'center' ? 'center' : 'left'}`);
    }
    return parts.length ? `;${parts.join(';')}` : '';
};

/**
 * Render one receipt copy's markup as HTML.
 *
 * `logoUrl` is passed separately rather than read from the markup so a caller
 * can print the body without it — the app does the same when a logo cannot be
 * fetched. A receipt without its logo is a cosmetic problem; a receipt that
 * does not print is a till that cannot serve a customer.
 */
export function starMarkupToHtml(markup, logoUrl = null) {
    if (!markup) return '';

    const out = [];
    let align = 'left';
    let bold = false;
    // Magnification is STATE, like align and bold — `[mag:]` applies to every
    // following line until another one changes it, which is how GS ! behaves
    // on paper and how both printer emitters already treat it.
    let magWidth = 1;
    let magHeight = 1;

    // One line of body text, carrying whatever align/bold/mag are currently
    // set to. Both text paths below go through here so a MARK-escaped line is
    // magnified exactly like an authored one — on the printer it would be,
    // since `parse` hands both to the emitter as plain text directives.
    const pushText = (value) => {
        const escaped = escapeHtml(value) || '&nbsp;';
        const style = `text-align:${align}${magStyle(magWidth, magHeight, align)}`;
        out.push(
            `<div style="${style}">`
            + `${bold ? `<strong>${escaped}</strong>` : escaped}</div>`,
        );
    };

    for (const rawLine of String(markup).split('\n')) {
        // Escaped server-side: text, verbatim, whatever it looks like.
        // Handled BEFORE any tag test rather than relying on the `^\[` anchors
        // to miss it, so the guarantee is stated rather than incidental.
        if (rawLine.startsWith(MARK)) {
            pushText(rawLine.slice(MARK.length));
            continue;
        }

        const line = rawLine;
        const stripped = line.trim();

        const alignMatch = ALIGN_RE.exec(stripped);
        if (alignMatch) {
            align = alignMatch[1] === 'left' ? 'left' : 'center';
            continue;
        }

        if (stripped === '[bold]') { bold = true; continue; }
        if (stripped === '[/bold]') { bold = false; continue; }

        const magMatch = MAG_RE.exec(stripped);
        if (magMatch) {
            const clamp = (raw) => Math.max(1, Math.min(MAX_MAGNIFY, parseInt(raw, 10)));
            magWidth = clamp(magMatch[1]);
            magHeight = clamp(magMatch[2]);
            continue;
        }

        if (stripped === '[brand]') {
            // The alt text is real, unlike the logo's: this one says something
            // a screen reader should read, and it is the same wording that is
            // drawn into the bitmap. No `t()` — the words are part of the
            // image, so this line is identical in Arabic mode by construction.
            out.push(
                `<img src="${BRAND_SRC}" alt="Powered by Aqana" `
                + `class="receipt-brand" style="${BRAND_STYLE}" `
                + `onerror="this.style.display='none'" />`,
            );
            continue;
        }

        // The paper cut has no browser equivalent; the page break between
        // copies is the caller's job.
        if (CUT_RE.test(stripped)) continue;

        const imageMatch = IMAGE_RE.exec(stripped);
        if (imageMatch) {
            // onerror hides a logo that 404s, matching what the receipt HTML
            // has always done for tenants whose configured logo never
            // resolved.
            const src = escapeHtml(logoUrl || imageMatch[1]);
            out.push(
                `<img src="${src}" alt="" class="receipt-logo" `
                + `style="${LOGO_STYLE}" `
                + `onerror="this.style.display='none'" />`,
            );
            continue;
        }

        // Deliberately the raw line, not the trimmed one: receipts
        // right-align prices with trailing spaces and indent add-on lines,
        // so trimming here would reflow every layout.
        pushText(line);
    }

    return out.join('');
}

export default starMarkupToHtml;
